import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { memo, useEffect, useState } from 'react'
import { Alert, Breadcrumb, Button, Card, Col, Container, Form, ListGroup, Modal, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom'
import { Groups } from '../../api/Groups';
import { Users } from '../../api/Users';
import AppLoader from '../../components/AppLoader';
import AppSearch from '../../components/AppSearch';
import IconButton from '../../components/IconButton';
import UserItem from '../../components/UserItem';
import { useAuth } from '../../contexts/auth';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import { useToasts } from '../../contexts/toasts';
import { GroupModel } from '../../models/Group';
import { User, UserRole } from '../../models/User';

type Props = {
  users: User[];
  target?: "group" | "available";
  onMoved?: Function;
  onAdded?: Function;
  onRemoved?: Function;
}

const ColumnUsersList = memo(({ users, target = "available", onMoved, onAdded, onRemoved }: Props) => {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState<string>("");
  const { loggedUser } = useAuth();

  const handleSearchTextChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const value = evt.target.value;
    setSearchText(value);
  }

  if (users.length === 0) return (
    <Alert className="py-2 mb-0">
      <FontAwesomeIcon icon={solid('info-circle')} />
      <span className="ms-2">No users found.</span>
    </Alert>
  )

  return (
    <>
      <AppSearch value={searchText} onChange={handleSearchTextChange} placeholder={t('usersList.search')} />

      {loggedUser && (
        <ListGroup className="mt-2">
          {users
            .filter(user => `${user.firstname} ${user.lastname}`.toLowerCase().includes(searchText.toLowerCase()))
            .map((user, index) => (
              <UserItem
                key={user.id}
                user={user}
                actions={
                  <>
                    {loggedUser!.role === UserRole.DOCTOR && (
                      <IconButton className="me-2" onClick={onMoved && onMoved!(user)}>
                        <FontAwesomeIcon icon={solid('share-square')} />
                      </IconButton>
                    )}
                    {
                      target === "available"
                        ? (
                          <IconButton className="text-success" onClick={onAdded!(user)}>
                            <FontAwesomeIcon icon={solid('plus')} />
                          </IconButton>
                        )
                        : (
                          <IconButton className="text-danger" onClick={onRemoved!(user)}>
                            <FontAwesomeIcon icon={solid('times')} />
                          </IconButton>
                        )
                    }
                  </>
                }
              />
            ))}
        </ListGroup>
      )}
    </>
  );
});

export default function GroupMembers() {
  const { t } = useTranslation();
  const { id } = useParams();

  const [userToMove, setUserToMove] = useState<User | null>();
  const [userToRemove, setUserToRemove] = useState<User | null>();
  const [users, setUsers] = useState<User[] | null>();
  const [group, setGroup] = useState<GroupModel>();
  const [groups, setGroups] = useState<GroupModel[]>();
  const [targetGroup, setTargetGroup] = useState<GroupModel>();

  const [showMoveModal, setShowMoveModal] = useState<boolean>();
  const [showRemoveModal, setShowRemoveModal] = useState<boolean>();
  const [validated, setValidated] = useState<boolean>();

  const { loggedUser } = useAuth();

  const { showLoader, hideLoader } = useFullScreenLoader();
  const { showToast } = useToasts();

  useEffect(() => {
    initGroup();
    initGroups();
    initUsers();
    // eslint-disable-next-line
  }, []);

  const initGroup = () => {
    Groups.getGroup(id!)
      .then(
        ({ data: group }) => setGroup(group),
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast!('group-error', message, `Group Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const initGroups = () => {
    Groups.getGroups()
      .then(
        ({ data: groups }) => setGroups(groups),
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast!('groups-error', message, `Groups Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const initUsers = () => {
    const params = new URLSearchParams([['roles', 'patient,doctor']]);

    Users.getUsers(params)
      .then(
        ({ data: users }) => setUsers(users.results),
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast!('users-error', message, `Users Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const reset = () => {
    setUsers(null);
    initUsers();
  }

  const handleMoveModalClose = () => {
    setShowMoveModal(false);
    setUserToMove(null);
    setTargetGroup(undefined);
  }

  const handleRemoveModalClose = () => {
    setShowRemoveModal(false);
    setUserToRemove(null);
  }

  const handleTargetGroupChange = (evt: React.ChangeEvent<HTMLSelectElement>) => {
    const value = evt.target.value;
    const relativeGroup = groups?.filter((g) => g.id === Number(value))!
    setTargetGroup(relativeGroup[0]!);
  }

  const handleSubmit = (evt: React.FormEvent<HTMLFormElement>) => {
    const form = evt.currentTarget;
    evt.preventDefault();
    evt.stopPropagation();
    setValidated(true);

    if (form.checkValidity() === true) {
      moveUser();
    }
  }

  const addUser = (user: User) => () => {
    showLoader!();

    Groups.addGroupMembers(id!, [user.id!])
      .then(
        () => {
          hideLoader!();
          showToast!('user-group-success', 'User successfully added to group', 'Group Updated', 'success');
          reset();
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          hideLoader!();
          showToast!('user-group-error', message, `Group Error ${status ? `(${status})` : ''}`, 'danger');
        }
      );
  }

  const openRemoveModal = (user: User) => () => {
    setUserToRemove(user);
    setShowRemoveModal(true);
  }

  const removeUser = () => {
    showLoader!();

    Groups.removeGroupMembers(id!, [userToRemove!.id!])
      .then(
        () => {
          hideLoader!();
          showToast!('user-group-success', 'User successfully removed from group', 'Group Updated', 'success');
          handleRemoveModalClose();
          reset();
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          hideLoader!();
          showToast!('user-group-error', message, `Group Error ${status ? `(${status})` : ''}`, 'danger');
        }
      );
  }

  const openMoveModal = (user: User) => () => {
    setUserToMove(user);
    setShowMoveModal(true);
  }

  const moveUser = () => {
    showLoader!();

    const movingUser: Partial<User> = {
      id: (userToMove as User).id,
      groups: [targetGroup!],
    };

    Users.updateUser(movingUser)
      .then(
        () => {
          hideLoader!();
          showToast!('user-success', 'User moved successfully', 'User Success', 'success');
          handleMoveModalClose();
          reset();
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          hideLoader!();
          showToast!('user-error', message, `User Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const renderUsers = () => {
    if (users!.length === 0) return (
      <Alert className="py-2 mb-0">
        <FontAwesomeIcon icon={solid('info-circle')} />
        <span className="ms-2">{t('usersList.noUsers')}</span>
      </Alert>
    )

    const groupUsers = users!.filter(user => user.groups?.map(g => g.id)?.includes(Number(id)!));
    const remainingUsers = users!.filter(user => !user.groups?.map(g => g.id)?.includes(Number(id)!)).filter((user) => (user.role === UserRole.PATIENT && (user.groups!?.length === 0 || !user.groups)) || user.role === UserRole.DOCTOR);

    return (
      <Card.Body>
        <Row className="gx-2">
          <Col>
            <h6>{t('groupMembers.title')}</h6>
            <ColumnUsersList target="group" users={groupUsers} onRemoved={openRemoveModal} onMoved={openMoveModal} />
          </Col>

          {loggedUser!.role !== UserRole.DOCTOR && (
            <Col>
              <h6>{t('groupMembers.availableUsers')}</h6>
              <ColumnUsersList users={remainingUsers} onAdded={addUser} />
            </Col>
          )}
        </Row>
      </Card.Body>
    )
  }

  const renderAsyncContent = () => {
    return users && group
      ? renderUsers()
      : (
        <div className="d-flex py-3 justify-content-center">
          <AppLoader size={150} />
        </div>
      )
  }

  return (
    <Container>
      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/groups' }}>{t('pages.groups')}</Breadcrumb.Item>
        <Breadcrumb.Item active>"{group?.name}" {t('common.members')}</Breadcrumb.Item>
      </Breadcrumb>

      <Card>{renderAsyncContent()}</Card>

      <Modal show={showMoveModal} onHide={handleMoveModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>{t('groupMembers.moveUser')}</Modal.Title>
        </Modal.Header>
        <Form validated={validated} noValidate onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group controlId="target-group">
              <Form.Label>{t('groupMembers.targetGroup')}</Form.Label>
              <Form.Select size="sm" value={targetGroup?.id} onChange={handleTargetGroupChange} required>
                <option value="" hidden disabled>{t('groupMembers.selectGroup')}</option>
                {groups
                  ?.filter(group => group.id !== id)
                  .map(group => (
                    <option key={group.id} value={group.id}>{group.name}</option>
                  ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">{t('errors.requiredField', { field: t('groupMembers.targetGroup') })}</Form.Control.Feedback>
            </Form.Group>

            <Alert className="py-2 mt-3 text-center" variant="warning">
              <FontAwesomeIcon size="2x" icon={solid('exclamation-triangle')} />
              <h6 className="ms-2">{t('groupMembers.moveWarning')}</h6>
            </Alert>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="light" className="border" size="sm" onClick={handleMoveModalClose}>{t('common.cancel')}</Button>
            <Button variant="success" size="sm" type="submit">{t('common.confirm')}</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showRemoveModal} onHide={handleRemoveModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>{t('groupMembers.removeUser')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t('groupMembers.removeQuestion')} <b>{userToRemove?.firstname} {userToRemove?.lastname}</b>?

          <Alert className="py-2 mt-3 text-center" variant="warning">
            <FontAwesomeIcon size="2x" icon={solid('exclamation-triangle')} />
            <h6 className="ms-2">{t('groupMembers.removeWarning')}</h6>
          </Alert>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="light" className="border" size="sm" onClick={handleRemoveModalClose}>{t('common.cancel')}</Button>
          <Button variant="success" size="sm" type="submit" onClick={removeUser}>{t('common.confirm')}</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}